import { useCallback, useEffect, useRef, useState } from "react";
import { RecipeApiError } from "./productRecipeApi";
import {
  Draft,
  DraftRow,
  RecipeState,
  fromRecipe,
} from "./productRecipeEditor";
import useRecipeDraftRecovery from "./useRecipeDraftRecovery";
import useRecipeNavigationGuard from "./useRecipeNavigationGuard";
import useRecipeIngredientSearch from "./useRecipeIngredientSearch";
import useRecipeResource from "./useRecipeResource";
import useRecipeMutations from "./useRecipeMutations";
export default function useProductRecipe(
  productId: number,
  productSellingUnit: number,
  active: boolean,
  onUnauthorized: () => void
) {
  const [state, setState] = useState<RecipeState>(() => ({
    recipe: null,
    version: null,
    draft: fromRecipe(null, productSellingUnit),
    baseline: "",
    editing: false,
    loaded: false,
    loading: false,
    saving: false,
    canManage: false,
    productAvailable: true,
    error: "",
    errors: {},
    conflict: false,
    notice: "",
  }));
  const patch = useCallback(
    (values: Partial<RecipeState>) =>
      setState((current) => ({ ...current, ...values })),
    []
  );
  const initialized = useRef(false);
  const alive = useRef(true);
  const busy = useRef(false);
  const nextKey = useRef(0);
  const auth = useRef(onUnauthorized);
  auth.current = onUnauthorized;
  const storageKey = `product-recipe-draft:${productId}`;
  const dirty =
    state.loaded &&
    state.editing &&
    JSON.stringify(state.draft) !== state.baseline;
  const disabled =
    !state.canManage ||
    state.saving ||
    state.conflict ||
    !state.productAvailable;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const fail = useCallback(
    (cause: unknown) => {
      if (!alive.current) return;
      if (cause instanceof RecipeApiError) {
        if (cause.status === 401) auth.current();
        if (cause.status === 403) patch({ canManage: false });
        patch({ errors: cause.fields });
        if (cause.status === 409) patch({ conflict: true });
      }
      patch({
        error:
          cause instanceof Error
            ? cause.message
            : "Could not complete the recipe request. Try again.",
      });
    },
    [patch]
  );
  const clearSearchError = useCallback(() => {
    if (alive.current) patch({ error: "" });
  }, [patch]);
  const { search, resetSearch } = useRecipeIngredientSearch(
    productId,
    fail,
    clearSearchError
  );
  const runtime = {
    productId,
    productSellingUnit,
    alive,
    busy,
    nextKey,
    patch,
    fail,
  };
  const load = useRecipeResource(runtime, storageKey, resetSearch);
  useEffect(() => {
    if (active && !initialized.current) {
      initialized.current = true;
      void load(true);
    }
  }, [active, load]);
  useRecipeDraftRecovery(
    storageKey,
    state.loaded,
    dirty,
    state.version,
    state.draft
  );
  useRecipeNavigationGuard(dirty, state.saving);
  const change = (values: Partial<Draft>) =>
    setState((current) => ({
      ...current,
      draft: { ...current.draft, ...values },
      errors: {},
      notice: "",
    }));
  const changeRow = (key: number, values: Partial<DraftRow>) =>
    setState((current) => ({
      ...current,
      draft: {
        ...current.draft,
        rows: current.draft.rows.map((row) =>
          row.key === key ? { ...row, ...values } : row
        ),
      },
      errors: {},
      notice: "",
    }));
  const { save, remove } = useRecipeMutations(runtime, state, disabled);
  const reload = () => {
    if (
      (dirty || state.conflict) &&
      !window.confirm("Discard your draft and reload the saved recipe?")
    )
      return;
    void load();
  };
  const discard = () => {
    if (
      busy.current ||
      !dirty ||
      !window.confirm("Discard your unsaved recipe changes?")
    )
      return;
    const draft = fromRecipe(state.recipe, productSellingUnit);
    nextKey.current = draft.rows.length;
    patch({
      draft,
      baseline: JSON.stringify(draft),
      version: state.recipe?.version ?? null,
      conflict: false,
      error: "",
      errors: {},
      notice: "Changes discarded.",
    });
  };
  const addIngredient = () => {
    const key = nextKey.current++;
    change({
      rows: [
        ...state.draft.rows,
        { key, product: null, quantity: "", unit: "kg", available: true },
      ],
    });
    requestAnimationFrame(() =>
      document.getElementById(`recipe-ingredient-${key}`)?.focus()
    );
  };
  const removeIngredient = (key: number) =>
    change({ rows: state.draft.rows.filter((row) => row.key !== key) });
  return {
    ...state,
    dirty,
    disabled,
    load,
    search,
    change,
    changeRow,
    save,
    remove,
    reload,
    addIngredient,
    removeIngredient,
    discard,
  };
}
